import { API } from "@swivl-mono/api-helper";
import { BOT_SEQUENCE_JIT_DEFAULT_FRESHNESS_MINUTES, filterClasses } from "@swivl-mono/helpers";
import {
  BOT_SEQUENCE_JIT_OBJECT,
  BotSequenceJitFreshnessUnit,
  BotSequenceType,
  FMSCapabilityState,
  FMSJitSettings,
  FMSJitSettingsResponse,
  FMSType
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Select, SelectItem, Switch } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { BiEdit } from "react-icons/bi";

import { Member } from "../../../../../Models/Member/Member.Current";
import { FreshnessChip, JIT_SEQUENCE_ORDER, PROVIDER_LABELS, UNIT_LABELS } from "../../../../Admin/FMSCapabilities/JitFreshness.Shared";

/**
 * "JIT freshness" section of the FMS integration page (ENG-3213): the Sequences engine's
 * per sequence-type JIT switch + freshness window for THIS integration, below "API
 * Credentials" and edited the same way (Edit → Cancel / Done). One row per sequence
 * type; where nothing is stored the row shows the defaults (JIT enabled, the global
 * window). Types the integration's FMS type cannot JIT-fetch (Capability Matrix) render
 * disabled with the reason.
 *
 * Self-contained on purpose: takes the integration's id + type, owns its load / draft /
 * save state and API calls, and knows nothing about the page layout or the FMS store —
 * moving it again only changes the mount point.
 */
interface Props {
  fmsId: number;
  fmsType: FMSType;
}

/** The window + switch of one sequence row, edited as a draft until Done. */
interface DraftCell {
  amount: number | null;
  unit: BotSequenceJitFreshnessUnit | null;
  enabled: boolean;
}

type DraftMap = Partial<Record<BotSequenceType, DraftCell>>;

const UNIT_OPTIONS: SelectItem[] = Object.values(BotSequenceJitFreshnessUnit).map((unit) => ({ value: unit, label: UNIT_LABELS[unit] }));

/**
 * api-helper wraps non-OK responses as `HTTP error <status>: <body>` — unwrap the body.
 * The server's errorHandler puts the real reason of a thrown Error in `error` (with a
 * generic "Internal Server Error" in `message`), so prefer `error` over `message`.
 */
function extractErrorReason(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const wrapped = message.match(/^HTTP error \d+: (.*)$/s);
  if (!wrapped) {
    return message;
  }
  try {
    const body = JSON.parse(wrapped[1]);
    const reason = body?.error ?? body?.message;
    return reason ? String(reason) : wrapped[1];
  } catch {
    return wrapped[1];
  }
}

function draftFromSettings(settings: FMSJitSettings): DraftMap {
  const draft: DraftMap = {};
  for (const sequenceType of JIT_SEQUENCE_ORDER) {
    const cell = settings[sequenceType];
    draft[sequenceType] = { amount: cell?.freshnessAmount ?? null, unit: cell?.freshnessUnit ?? null, enabled: cell?.enabled ?? true };
  }
  return draft;
}

function isCapable(data: FMSJitSettingsResponse, sequenceType: BotSequenceType): boolean {
  return data.capabilities[sequenceType] === FMSCapabilityState.Implemented;
}

export const FMSJitSettingsSection = ({ fmsId, fmsType }: Props) => {
  const orgId = Member.orgId!;
  const fmsLabel = PROVIDER_LABELS[fmsType] ?? fmsType;

  const [data, setData] = useState<FMSJitSettingsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<DraftMap>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setIsEditing(false);
    const load = async (): Promise<void> => {
      try {
        const response = await API.FMS.getJitSettings(orgId, fmsId);
        if (!cancelled) setData(response);
      } catch (error) {
        if (!cancelled) setLoadError(extractErrorReason(error));
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgId, fmsId]);

  const handleEdit = (): void => {
    if (!data) return;
    setDraft(draftFromSettings(data.settings));
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setDraft({});
    setSaveError(null);
    setIsEditing(false);
  };

  const updateCell = (sequenceType: BotSequenceType, patch: Partial<DraftCell>): void => {
    setDraft((prev) => ({ ...prev, [sequenceType]: { ...(prev[sequenceType] as DraftCell), ...patch } }));
  };

  const handleDone = async (): Promise<void> => {
    if (!data) return;
    // Every configurable type is sent whole (the server removes all-defaults cells);
    // capability-blocked types are never sent — the server would reject them.
    const settings: FMSJitSettings = {};
    for (const sequenceType of JIT_SEQUENCE_ORDER) {
      const cell = draft[sequenceType];
      if (!cell || !isCapable(data, sequenceType)) continue;
      settings[sequenceType] = { freshnessAmount: cell.amount, freshnessUnit: cell.unit, enabled: cell.enabled };
    }
    setSaveError(null);
    try {
      const response = await API.FMS.updateJitSettings(orgId, fmsId, { settings });
      setData(response);
      setDraft({});
      setIsEditing(false);
    } catch (error) {
      setSaveError(extractErrorReason(error));
    }
  };

  const renderRows = (): React.ReactNode => {
    if (loadError) {
      return <p className="py-2 text-sm text-red-600">{loadError}</p>;
    }
    if (!data) {
      return <p className="py-2 text-sm text-grey-500">Loading…</p>;
    }
    if (!isEditing) {
      return JIT_SEQUENCE_ORDER.map((sequenceType) => (
        <div key={sequenceType} className="flex items-center py-2">
          <label className="min-w-[140px] font-mono text-sm font-medium text-black">{sequenceType}</label>
          <div className="flex-1">
            <FreshnessChip capability={data.capabilities[sequenceType]} setting={data.settings[sequenceType]} defaultMinutes={data.defaultMinutes} />
          </div>
        </div>
      ));
    }
    return (
      <div className="flex flex-col gap-4">
        {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        {JIT_SEQUENCE_ORDER.map((sequenceType, index) => (
          <div key={sequenceType} className="flex flex-col gap-3">
            {index > 0 && <div className="border-t border-grey-200" />}
            <SequenceEditor
              fmsLabel={fmsLabel}
              sequenceType={sequenceType}
              capable={isCapable(data, sequenceType)}
              capability={data.capabilities[sequenceType]}
              defaultMinutes={data.defaultMinutes}
              cell={draft[sequenceType] as DraftCell}
              onChange={(patch) => updateCell(sequenceType, patch)}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between">
        <h2>JIT freshness</h2>
        {isEditing ? (
          <div className="flex gap-2">
            <Button color={ButtonColor.WHITE} onClick={handleCancel}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} onClickAsync={handleDone}>
              Done
            </Button>
          </div>
        ) : (
          <Button color={ButtonColor.WHITE} onClick={handleEdit} disabled={!data}>
            Edit <BiEdit />
          </Button>
        )}
      </div>
      At fire time the Sequences engine re-fetches the entity from {fmsLabel} unless the location&apos;s data is fresher than the JIT freshness
      window. Both the window and an explicit JIT on/off switch are configurable per sequence type for this integration; where nothing is set, the
      defaults apply — JIT enabled with a {data?.defaultMinutes ?? BOT_SEQUENCE_JIT_DEFAULT_FRESHNESS_MINUTES} min window.
      <div className="apiValues">{renderRows()}</div>
    </div>
  );
};

interface SequenceEditorProps {
  fmsLabel: string;
  sequenceType: BotSequenceType;
  capable: boolean;
  capability: FMSCapabilityState | undefined;
  defaultMinutes: number;
  cell: DraftCell;
  onChange: (patch: Partial<DraftCell>) => void;
}

function SequenceEditor({ fmsLabel, sequenceType, capable, capability, defaultMinutes, cell, onChange }: SequenceEditorProps) {
  const objectType = BOT_SEQUENCE_JIT_OBJECT[sequenceType];

  if (!capable) {
    return (
      <div className="flex flex-col gap-2">
        <p className="font-mono text-sm font-bold text-grey-500">{sequenceType}</p>
        <div className="flex items-center gap-3">
          <span className="w-20 shrink-0 text-sm font-semibold text-grey-400">JIT</span>
          <Switch on={false} isDisabled />
          <span className="text-sm text-grey-500">
            Not configurable — the capability matrix marks JIT for {fmsLabel} {objectType} as &quot;{capability ?? FMSCapabilityState.TBD}&quot;, so
            the engine never re-fetches {objectType?.toLowerCase()}s from {fmsLabel}; fires proceed on cached data.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="font-mono text-sm font-bold text-grey-900">{sequenceType}</p>
      <div className="flex items-center gap-3">
        <span className="w-20 shrink-0 text-sm font-semibold text-grey-600">JIT</span>
        <Switch on={cell?.enabled ?? true} onSet={(on) => onChange({ enabled: on })} />
        <span className={filterClasses("text-sm", cell?.enabled ? "text-grey-900" : "text-grey-500")}>
          {cell?.enabled ? "Enabled" : "Disabled — fires proceed on cached data"}
        </span>
      </div>

      {cell?.enabled && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="w-20 shrink-0 text-sm font-semibold text-grey-600">Freshness</span>
          {cell.amount == null ? (
            <>
              <span className="text-sm text-grey-900">{defaultMinutes} min</span>
              <span className="rounded bg-grey-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-grey-600 uppercase">default</span>
              <Button
                variant={ButtonVariant.GHOST}
                size={ButtonSize.SMALL}
                onClick={() => onChange({ amount: defaultMinutes, unit: BotSequenceJitFreshnessUnit.MINUTES })}
              >
                Override
              </Button>
            </>
          ) : (
            <>
              <input
                type="number"
                min={1}
                value={cell.amount}
                onChange={(event) => {
                  const amount = parseInt(event.target.value, 10);
                  if (amount > 0) onChange({ amount });
                }}
                className="h-8 w-20 rounded border border-grey-300 bg-white px-2 text-right text-sm text-grey-900 focus:border-orange-400 focus:outline-none"
              />
              <div className="w-32">
                <Select
                  options={UNIT_OPTIONS}
                  selectedValues={[cell.unit ?? BotSequenceJitFreshnessUnit.MINUTES]}
                  hideClear
                  onChange={(values) => onChange({ unit: values[0] as BotSequenceJitFreshnessUnit })}
                />
              </div>
              <span className="rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-orange-700 uppercase">
                custom
              </span>
              <Button variant={ButtonVariant.TEXT} size={ButtonSize.SMALL} onClick={() => onChange({ amount: null, unit: null })}>
                Use default ({defaultMinutes} min)
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
